import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { loginWithDevToken } from '@/devAuth';
import styles from './LoginPage.module.scss';

// OAuth login : TOUJOURS l'API en direct (jamais le proxy Cloudflare /api/*, qui suit les 302 et casserait le flux).
const RECUBE_LOGIN_URL = 'https://api-staff.be.recube.gg/api/auth/recube/login';

const RECUBE_ERRORS: Record<string, string> = {
  token_exchange: "Échec de l'échange du code Recube.",
  userinfo: 'Impossible de lire ton profil Recube.',
  profil_incomplet: 'Profil Recube incomplet (pseudo manquant).',
  state_invalide: 'Session de connexion expirée, réessaie.',
  no_access_token: 'Recube n\'a pas renvoyé de token.',
  exception: 'Erreur pendant la connexion Recube.',
  missing_code: 'Code d\'autorisation manquant.',
};

export function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devToken, setDevToken] = useState('');
  const [devLoading, setDevLoading] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Retour du backend après login Recube : #token=... (succès) ou #error=... (échec).
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    const p = new URLSearchParams(hash.slice(1));
    const token = p.get('token');
    const err = p.get('error');
    // Nettoie le fragment (ne pas laisser le token dans l'URL).
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    if (err) {
      setError(RECUBE_ERRORS[err] ?? `Connexion Recube échouée (${err}).`);
      return;
    }
    if (token) {
      setIsLoading(true);
      loginWithDevToken(token).then((r) => {
        if (r.ok) navigate('/dashboard', { replace: true });
        else { setError(r.error ?? 'Échec de la connexion Recube.'); setIsLoading(false); }
      });
    }
  }, [navigate]);

  // Déjà connecté → dashboard.
  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  const handleDevLogin = async () => {
    setDevLoading(true);
    setError(null);
    const result = await loginWithDevToken(devToken);
    if (result.ok) {
      navigate('/dashboard', { replace: true });
    } else {
      setError(result.error ?? 'Échec de la connexion dev.');
      setDevLoading(false);
    }
  };

  const handleRecubeLogin = () => {
    setIsLoading(true);
    setError(null);
    // Redirige vers le backend qui lance le flux OAuth Recube (PKCE côté serveur).
    window.location.href = RECUBE_LOGIN_URL;
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <img src="/recube-logo.png" alt="Recube" className={styles.logo} />
          <h1 className={styles.title}>Staff Panel</h1>
          <p className={styles.subtitle}>
            Connectez-vous avec votre compte Recube pour accéder au panel d'administration.
          </p>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <button className={styles.xboxButton} onClick={handleRecubeLogin} disabled={isLoading}>
          {isLoading ? (
            <span className={styles.loader}>
              <span />
              <span />
              <span />
            </span>
          ) : (
            <>
              <img src="/recube-logo.png" alt="Recube" className={styles.xboxLogo} />
              <span>Se connecter avec Recube</span>
            </>
          )}
        </button>

        <p className={styles.footer}>
          Seuls les membres du staff autorisés peuvent accéder à ce panel.
        </p>

        {import.meta.env.DEV && (
          <div
            style={{
              marginTop: 24,
              paddingTop: 16,
              borderTop: '1px dashed rgba(255,255,255,0.2)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <label style={{ fontSize: 12, opacity: 0.7 }}>
              🛠️ Connexion locale (dev) — collez un token JWT valide (clé <code>recubebe_xbox_token</code>) :
            </label>
            <textarea
              value={devToken}
              onChange={(e) => setDevToken(e.target.value)}
              placeholder="eyJhbGciOi..."
              rows={3}
              spellCheck={false}
              style={{
                width: '100%',
                fontFamily: 'monospace',
                fontSize: 12,
                padding: 8,
                borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(0,0,0,0.3)',
                color: 'inherit',
                resize: 'vertical',
              }}
            />
            <button
              type="button"
              onClick={handleDevLogin}
              disabled={devLoading || !devToken.trim()}
              style={{
                padding: '8px 12px',
                borderRadius: 6,
                cursor: devLoading || !devToken.trim() ? 'not-allowed' : 'pointer',
                opacity: devLoading || !devToken.trim() ? 0.5 : 1,
              }}
            >
              {devLoading ? 'Connexion...' : 'Se connecter (dev)'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
